/**
 * LoginPage.tsx — Admin console login page
 *
 * Purpose:
 *   Initiates OIDC PKCE login for billing operators. Direct access to
 *   finance.rule42.ai should authenticate against rule42-auth rather than a
 *   dead local billing login endpoint.
 *
 * @module packages/billing-admin/src/pages/LoginPage
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { clearPostLogout, isPostLogout } from '../lib/admin-oidc';

interface LoginLocationState {
  returnTo?: string;
  autoStart?: boolean;
}

/**
 * LoginPage — renders an OIDC sign-in entry point for billing admin.
 *
 * Redirects to /revenue immediately if already authenticated.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export default function LoginPage(): React.JSX.Element {
  const { login, loading, error, token } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const autoStartedRef = useRef(false);
  const locationState = (location.state as LoginLocationState | null) ?? null;
  const returnTo = locationState?.returnTo ?? '/overview';
  const autoStart = locationState?.autoStart ?? false;
  const postLogout = isPostLogout();

  // ARCHITECTURE: Redirect immediately if session token already present
  useEffect(() => {
    if (token && !postLogout) navigate(returnTo, { replace: true });
  }, [postLogout, returnTo, token, navigate]);

  const handleLogin = useCallback(async (): Promise<void> => {
    try {
      clearPostLogout();
      await login(returnTo, { prompt: 'login' });
    } catch {
      // Error is captured in AdminAuthContext.error — no additional handling
    }
  }, [login, returnTo]);

  useEffect(() => {
    if (!token && autoStart && !autoStartedRef.current && !postLogout) {
      autoStartedRef.current = true;
      void handleLogin();
    }
  }, [autoStart, handleLogin, postLogout, token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.18),_transparent_28%),linear-gradient(180deg,_#020617_0%,_#0f172a_52%,_#e2e8f0_52%,_#f8fafc_100%)] px-6">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-slate-950/85 p-8 text-white shadow-[0_30px_80px_-40px_rgba(15,23,42,0.85)] backdrop-blur">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-300/80">Rule42 Billing</p>
        <h1 className="mt-4 text-3xl font-semibold text-white">Sign in to Billing</h1>
        <p className="mt-3 text-sm leading-6 text-slate-300">
          Authenticate directly against rule42-auth. Existing bootstrap access from rule42-cloud still works, but direct entry on finance.rule42.ai now uses the same OIDC product flow.
        </p>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
            {error}
          </div>
        )}

        <div className="mt-8 space-y-4">
          <button
            type="button"
            onClick={() => {
              void handleLogin();
            }}
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading && <LoadingSpinner size="sm" />}
            {loading ? 'Redirecting…' : 'Continue with rule42'}
          </button>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
            <p className="font-medium text-white">Access model</p>
            <p className="mt-2 leading-6">
              Billing admin requires either the `billing_admin` authorization role or platform admin access from rule42-auth.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
